import React from 'react'
import { createUseStyles } from 'react-jss'
import breakpoints from '../../../theme/breakpoints'
import { Loader } from '../Loaders/Spinner'

export interface Props {
    onClick?: () => void
    type?:
        | 'primary'
        | 'secondary'
        | 'danger'
        | 'warning'
        | 'action'
        | 'ghost'
        | 'dropdown'
    size?: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge'
    disabled?: boolean
    icon?: JSX.Element
    buttonType?: 'button' | 'submit'
    style?: React.CSSProperties
    loading?: boolean
    className?: string
}

export interface ButtonLinkProps {
    type?: 'primary' | 'secondary' | 'danger' | 'warning' | 'action'
    href?: string
    target?: string
    icon?: JSX.Element
    size?: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge'
}

const BUTTON_CONTAINER = 'button'

const useStyles = createUseStyles((theme) => ({
    [BUTTON_CONTAINER]: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.5em 1.75em',
        margin: '0 0.3em 0.3em 0',
        borderRadius: '6px',
        boxSizing: 'border-box',
        textDecoration: 'none',
        fontWeight: 400,
        textAlign: 'center',
        transition: 'all 0.2s',
        boxShadow: 0,
        '&:hover': {
            cursor: 'pointer',
        },
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',
        border: 0,
    },

    small: {
        fontSize: '.75em',
        padding: '.2rem 1rem',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                fontSize: '.875em',
            },
        },
    },

    medium: {
        fontSize: '.875em',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                fontSize: '.875em',
            },
        },
    },

    large: {
        fontSize: '1em',

        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                fontSize: '1rem',
                minHeight: '48px',
            },
        },
    },
    xlarge: {
        fontSize: '1em',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                fontSize: '1.125rem',
            },
        },
    },
    xxlarge: {
        fontSize: '1em',
        minHeight: '56px',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                fontSize: '1.145rem',
            },
        },
    },

    action: {
        composes: `$${BUTTON_CONTAINER}`,
        backgroundColor: theme.colors.white,
        color: theme.colors.black,
        padding: 0,
        margin: 0,
        height: 'auto',
        width: 'auto',
        display: 'block',
    },

    ghost: {
        composes: `$${BUTTON_CONTAINER}`,
        backgroundColor: 'transparent',
        color: theme.colors.black,
        padding: 0,
        margin: 0,
        height: 'auto',
        width: 'auto',
        display: 'block',
        '&:hover': {
            backgroundColor: 'transparent',
        },
        '&:active': {
            backgroundColor: 'transparent',
        },
    },

    primary: () => ({
        composes: `$${BUTTON_CONTAINER}`,
        backgroundColor: theme.colors.green500,
        color: theme.colors.white,
        '& > svg': {
            fill: theme.colors.white,
        },
        '&:hover': {
            backgroundColor: theme.colors.green600,
        },
        '&:focus, &:active': {
            outline: `2px solid ${theme.colors.white}`,
            outlineOffset: '-2px',
        },
        '&:disabled': {
            opacity: 0.3,
            cursor: 'not-allowed',
        },
    }),
    secondary: {
        composes: `$${BUTTON_CONTAINER}`,
        backgroundColor: theme.colors.white,
        color: theme.colors.black,
        border: '1px solid #DDDDDD',
        '& > svg': {
            fill: theme.colors.green500,
        },
        '&:hover': {
            backgroundColor: theme.colors.green600,
            color: theme.colors.white,
            '&:hover svg': {
                fill: theme.colors.white,
            },
        },
        '&:active': {
            outline: `2px solid ${theme.colors.green600}`,
            backgroundColor: theme.colors.white,
            outlineOffset: '-2px',
            color: theme.colors.black,
        },
        '&:disabled': {
            opacity: 0.3,
            cursor: 'not-allowed',
        },
    },
    danger: {
        composes: `$${BUTTON_CONTAINER}`,
        backgroundColor: theme.colors.red500,
        color: theme.colors.white,
        border: '1px solid #DDDDDD',
        '&:hover': {
            backgroundColor: theme.colors.red600,
            color: theme.colors.white,
        },
        '&:active': {
            outline: `2px solid ${theme.colors.red600}`,
            backgroundColor: theme.colors.white,
            outlineOffset: '-2px',
            color: theme.colors.black,
        },
        '&:disabled': {
            opacity: 0.3,
            cursor: 'not-allowed',
        },
    },
    warning: {
        composes: `$${BUTTON_CONTAINER}`,
        backgroundColor: theme.colors.orange500,
        color: theme.colors.white,
        '&:hover': {
            backgroundColor: theme.colors.orange600,
            color: theme.colors.white,
        },
        '&:focus, &:active': {
            outline: `2px solid ${theme.colors.orange600}`,
            backgroundColor: theme.colors.white,
            outlineOffset: '-2px',
            color: theme.colors.black,
        },
        '&:disabled': {
            opacity: 0.3,
            cursor: 'not-allowed',
        },
    },
    icon: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        marginRight: '.5rem',
        '& svg': {
            width: '1.75em',
        },
    },
    dropdown: {
        composes: `$${BUTTON_CONTAINER}`,
        backgroundColor: theme.colors.white,
        color: theme.colors.black,
        padding: 0,
        margin: 0,
        height: 'auto',
        width: 'auto',
        display: 'block',
        borderRadius: 0,
        '&:hover': {
            backgroundColor: theme.colors.green500,
            color: theme.colors.white,
            '&:hover svg': {
                fill: theme.colors.white,
            },
        },
    },
}))

const Button: React.FC<Props> = (
    {
        children,
        onClick,
        type = 'primary',
        size = 'medium',
        disabled = false,
        icon,
        buttonType = 'button',
        style,
        loading = false,
        className,
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref
) => {
    const classes = useStyles()

    return (
        <button
            className={`${classes[type]} ${classes[size]} ${className || ''}`}
            onClick={onClick}
            type={buttonType === 'button' ? 'button' : 'submit'}
            disabled={disabled || loading}
            style={style}
        >
            {loading ? (
                <span className={classes.icon}>
                    <Loader />
                </span>
            ) : (
                <>
                    {icon && <span className={classes.icon}>{icon}</span>}
                    {children}
                </>
            )}
        </button>
    )
}

const ButtonLink: React.FC<ButtonLinkProps> = (
    { children, size = 'medium', icon, type, href, target },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref
) => {
    const classes = useStyles()

    return (
        <a
            className={`${classes[type]} ${classes[size]}`}
            href={href}
            target={target}
        >
            {icon && <span className={classes.icon}>{icon}</span>}
            {children}
        </a>
    )
}

export { Button, ButtonLink }
